import { useCallback, useEffect, useMemo, useState } from "react";
import type { VisibilityState } from "@tanstack/react-table";
import type { ColumnId, ColumnVisibility } from "../columnConfig";
import { DEFAULT_COLUMN_VISIBILITY } from "../columnConfig";

const STORAGE_KEY = "companyStaffColumnVisibility";

function loadColumnVisibility(): VisibilityState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return getDefaultVisibilityState();

    const parsed = JSON.parse(stored) as Record<string, boolean>;

    const defaultState = getDefaultVisibilityState();
    const merged: VisibilityState = { ...defaultState };

    Object.keys(defaultState).forEach((key) => {
      if (key in parsed && typeof parsed[key] === "boolean") {
        merged[key] = parsed[key];
      }
    });

    return merged;
  } catch (error) {
    console.warn("Failed to load column visibility from localStorage:", error);
    return getDefaultVisibilityState();
  }
}

function saveColumnVisibility(visibility: VisibilityState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibility));
  } catch (error) {
    console.warn("Failed to save column visibility to localStorage:", error);
  }
}

function getDefaultVisibilityState(): VisibilityState {
  return Object.fromEntries(
    Object.entries(DEFAULT_COLUMN_VISIBILITY).map(([key, value]) => [key, value])
  );
}

export function useColumnVisibilityStorage() {
  const [columnVisibility, setColumnVisibilityState] =
    useState<VisibilityState>(() => loadColumnVisibility());

  useEffect(() => {
    saveColumnVisibility(columnVisibility);
  }, [columnVisibility]);

  const setColumnVisibility = useCallback(
    (
      updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)
    ) => {
      setColumnVisibilityState((prev) => {
        const newVisibility =
          typeof updater === "function" ? updater(prev) : updater;
        return newVisibility;
      });
    },
    []
  );

  const columnVisibilityRecord = useMemo<ColumnVisibility>(() => {
    return Object.fromEntries(
      Object.entries(columnVisibility).map(([key, value]) => [
        key as ColumnId,
        value !== false,
      ])
    ) as ColumnVisibility;
  }, [columnVisibility]);

  return {
    columnVisibility,
    columnVisibilityRecord,
    setColumnVisibility,
  };
}
